import type { ColumnDef } from '@tanstack/react-table'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { TextWithTooltip } from '@/components/custom/text-with-tooltip'
import { Badge } from '@/components/ui/badge'
import type { PatientRecord } from '@/features/patients/types'
import {
  mergeSourceIds,
  packSourceIds,
} from '@/features/patients/utils/sourceIds'

const Unavailable = () => <span className="text-muted-foreground">-</span>

export function patientFullName(patient: PatientRecord): string {
  return [patient.first_name, patient.middle_name, patient.last_name]
    .filter((part) => part && part.trim().length > 0)
    .join(' ')
}

/** Parse a YYYY-MM-DD date as a local calendar date (no timezone shift). */
export function formatDateOfBirth(dob: string | null): string | null {
  if (!dob) return null
  const parts = dob.split('-').map((p) => parseInt(p, 10))
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null
  const [year, month, day] = parts as [number, number, number]
  const date = new Date(year, month - 1, day)
  // new Date normalizes out-of-range parts (Feb 30 → Mar 1); reject those so
  // an invalid DOB falls back to the placeholder instead of a shifted date.
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Width of the Source ID cell's content box, matching `w-32` below. Shared with
 * the packer so the badge fitting and the rendered box cannot drift apart.
 */
const SOURCE_ID_CELL_WIDTH = 128

/**
 * The client's identifiers, sized to sit alongside Phone and Date of Birth
 * rather than absorbing the row.
 *
 * A client carries up to five; rendering them all made this the widest column
 * in the table. As many as fit are shown and the rest counted, so a client with
 * two short identifiers gets both while a long one gets a single badge. The
 * full list lives in the details sheet's Identifiers tab.
 */
export function SourceIdCell({
  sourceIds,
}: {
  sourceIds?: Record<string, string>
}) {
  const entries = mergeSourceIds(sourceIds)
  const { shown, remaining } = packSourceIds(entries, SOURCE_ID_CELL_WIDTH)
  if (shown.length === 0) return <Unavailable />

  return (
    <div className="flex w-32 items-center gap-1 overflow-hidden">
      {shown.map((entry) => (
        <Badge
          key={`${entry.system}:${entry.value}`}
          variant="secondary"
          // `shrink` overrides Badge's `shrink-0`: the id truncates to make
          // room, so a badge never overflows the cell or hides the "+N".
          className="min-w-0 shrink border-transparent bg-sky-50 text-sky-700"
          title={`${entry.label} ${entry.value}`}>
          <span className="shrink-0 font-medium">{entry.shortLabel}</span>
          <span className="truncate">{entry.value}</span>
        </Badge>
      ))}
      {remaining > 0 ? (
        <Badge
          variant="secondary"
          className="shrink-0 border-transparent bg-sky-50 text-sky-700"
          title={entries
            .slice(shown.length)
            .map((entry) => `${entry.label} ${entry.value}`)
            .join('\n')}>
          +{remaining}
        </Badge>
      ) : null}
    </div>
  )
}

/** v3 Patients table: 6 at-a-glance columns; the rest live in the details sheet. */
export const patientsColumns: ColumnDef<PatientRecord>[] = [
  {
    id: 'source_id',
    accessorFn: (row) => mergeSourceIds(row.source_ids)[0]?.value ?? '',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Source ID" />
    ),
    enableHiding: false,
    cell: ({ row }) => <SourceIdCell sourceIds={row.original.source_ids} />,
  },
  {
    id: 'patient_name',
    accessorFn: patientFullName,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Client Name" />
    ),
    enableHiding: false,
    cell: ({ getValue }) => (
      <div className="truncate">{getValue<string>() || <Unavailable />}</div>
    ),
  },
  {
    accessorKey: 'date_of_birth',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date of Birth" />
    ),
    cell: ({ row }) => (
      <div className="whitespace-nowrap">
        {formatDateOfBirth(row.original.date_of_birth) ?? <Unavailable />}
      </div>
    ),
  },
  {
    accessorKey: 'phone',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Phone" />
    ),
    cell: ({ row }) => (
      <div className="whitespace-nowrap">
        {row.original.phone || <Unavailable />}
      </div>
    ),
  },
  {
    accessorKey: 'address',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Address" />
    ),
    cell: ({ row }) => {
      const value = row.original.address
      if (!value) return <Unavailable />
      // w-full makes Address the flexible column under content-fit layout: the
      // other columns shrink to their content and Address absorbs the slack
      // (instead of it pooling on Source ID), truncating only when genuinely long.
      return <TextWithTooltip text={value} className="w-full" />
    },
  },
  {
    accessorKey: 'is_active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Active" />
    ),
    cell: ({ row }) => {
      const value = row.original.is_active
      if (value === null || value === undefined) return <Unavailable />
      return (
        <div className="whitespace-nowrap">{value ? 'Active' : 'Inactive'}</div>
      )
    },
  },
]
